"use client";

import { CalendarCheck, Funnel as Filter, CircleQuestionMark as HelpCircle, MessageSquare, MoonStar, PhoneCall, Plug, Send, Share2, TrendingUp } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Automation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const capabilities = [{
    name: "Answers Calls 24/7",
    description: "Handles inbound voice calls day and night, speaking naturally and answering questions without delay.",
    channel: "Phone Voice",
    impact: "Zero Missed Calls",
    Icon: PhoneCall
  }, {
    name: "Instant Web Replies",
    description: "Engages website visitors in real-time, responding in seconds before they leave for a competitor.",
    channel: "Website Chat",
    impact: "Instant Engagement",
    Icon: MessageSquare
  }, {
    name: "Answers Common Questions",
    description: "Provides instant, accurate information on pricing, service areas, opening hours, and policies.",
    channel: "Voice & Chat",
    impact: "Saves Staff Time",
    Icon: HelpCircle
  }, {
    name: "Qualifies Leads",
    description: "Asks key screening questions to filter out spam and prioritize high-value prospective clients.",
    channel: "Inbound Flows",
    impact: "High Intent Only",
    Icon: Filter
  }, {
    name: "Books Appointments",
    description: "Checks real-time availability and schedules consultations directly into your calendar software.",
    channel: "Direct Sync",
    impact: "Automated Booking",
    Icon: CalendarCheck
  }, {
    name: "Follow-up Texts & Emails",
    description: "Sends immediate booking confirmations, reminders, and follow-up links via SMS and email.",
    channel: "SMS / Email",
    impact: "Higher Conversion",
    Icon: Send
  }, {
    name: "Smart Call Routing",
    description: "Transfers urgent or complex calls to specific team members or departments automatically.",
    channel: "Voice Routing",
    impact: "Fast Escalation",
    Icon: Share2
  }, {
    name: "After-Hours Capture",
    description: "Captures lead details and schedules call-backs when your team is away or closed for the weekend.",
    channel: "24/7 Active",
    impact: "Never Miss Lead",
    Icon: MoonStar
  }, {
    name: "Fewer Missed Calls",
    description: "Eliminates voicemail black holes by engaging callers immediately with instant interactive response.",
    channel: "Voice & SMS",
    impact: "+35% Response Rate",
    Icon: TrendingUp
  }, {
    name: "Full Integrations",
    description: "Pushes captured leads, call transcripts, and booking logs directly into your existing CRM tools.",
    channel: "CRM / Tools",
    impact: "Seamless Data Flow",
    Icon: Plug
  }];
  return <section data-section-id="2254" id="aiautomationoverview" className="bg-background text-foreground py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-6">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-6" style={{
            color: "var(--accent-deep-red)"
          }}>
              Capabilities
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-foreground">
              Everything it handles, on its own.
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-4">
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-xl">
              Our AI receptionist and AI chat work across phone, website, and messaging — capturing, qualifying, and converting every enquiry, then handing clean data to your team.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {capabilities.map((item, i) => {
          const Icon = item.Icon;
          return <div data-index={i} key={item.name} className="group relative bg-card p-7 md:p-8 border border-border hover:border-[var(--accent-deep-red)] transition-all duration-300 ease-out hover:-translate-y-1 shadow-sm" style={{
            backgroundColor: "var(--card)"
          }}>
                <span className="absolute top-6 right-6 font-serif italic text-2xl text-muted-foreground/30 group-hover:text-[var(--accent-deep-red)] transition-colors duration-300">
                  {String(i + 1).padStart(2, "0")}
                </span>

                <div className="mb-6">
                  <Icon className="w-9 h-9 transition-colors duration-300" strokeWidth={1.25} style={{
                color: "var(--accent-deep-red)"
              }} />
                </div>

                <h3 className="font-serif font-default text-2xl leading-tight text-card-foreground mb-3">
                  {item.name}
                </h3>

                <p className="text-sm leading-relaxed text-muted-foreground mb-8">
                  {item.description}
                </p>

                <div className="pt-5 border-t border-border">
                  <p className="font-mono text-xs tracking-wider uppercase">
                    <span style={{
                  color: "var(--accent-deep-red)"
                }}>CHANNEL:</span>{" "}
                    <span className="text-foreground">{item.channel}</span>
                    <span className="mx-2 opacity-50">·</span>
                    <span style={{
                  color: "var(--accent-deep-red)"
                }}>IMPACT:</span>{" "}
                    <span className="text-foreground">{item.impact}</span>
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}